import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './BusinessDetails.css';

const BusinessDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBusinessDetails();
  }, [id]);

  const fetchBusinessDetails = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${id}`);
      setBusiness(response.data.business);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="business-details-page">
          <Loader message="Loading business details..." variant="page" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="business-details-page">
          <div className="error-banner">{error}</div>
          <button onClick={() => navigate('/businesses')} className="back-button">
            ← Back to Businesses
          </button>
        </div>
      </Layout>
    );
  }

  if (!business) {
    return (
      <Layout>
        <div className="business-details-page">
          <div className="error-banner">Business not found</div>
          <button onClick={() => navigate('/businesses')} className="back-button">
            ← Back to Businesses
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="business-details-page">
        <div className="page-header">
          <div>
            <button onClick={() => navigate('/businesses')} className="back-button">
              ← Back to Businesses
            </button>
            <h1>Business Details</h1>
            <p className="page-subtitle">View complete information about this business</p>
          </div>
        </div>

        <div className="details-container">
          <div className="details-card">
            <div className="card-header">
              <h2>{business.name}</h2>
              <span className={`status-badge ${business.is_active ? 'active' : 'inactive'}`}>
                {business.is_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>

            <div className="details-section">
              <h3>Basic Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Business Code</label>
                  <p className="code-cell">BUS-{String(business.id).padStart(3, '0')}</p>
                </div>
                <div className="detail-item">
                  <label>Name</label>
                  <p>{business.name}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{business.email || '—'}</p>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <p>{business.phone || '—'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Address</label>
                  <p>{business.address || '—'}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Description</label>
                  <p>{business.description || '—'}</p>
                </div>
                <div className="detail-item">
                  <label>Created At</label>
                  <p>{formatDate(business.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3>Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{business.branches?.length || 0}</div>
                  <div className="stat-label">Branches</div>
                  <button 
                    className="view-link"
                    onClick={() => navigate(`/businesses/${business.id}/branches`)}
                  >
                    View All →
                  </button>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{business.users?.length || 0}</div>
                  <div className="stat-label">Total Users</div>
                  <button 
                    className="view-link"
                    onClick={() => navigate(`/businesses/${business.id}/users`)}
                  >
                    View All →
                  </button>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{business.users?.filter(u => u.role === 'admin').length || 0}</div>
                  <div className="stat-label">Admins</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{business.users?.filter(u => u.role === 'manager').length || 0}</div>
                  <div className="stat-label">Managers</div>
                </div>
              </div>
            </div>

            <div className="details-section">
              <div className="section-header">
                <h3>Branches</h3>
                {user?.role === 'super_admin' && (
                  <button
                    className="create-button"
                    onClick={() => navigate(`/businesses/${business.id}/branches`)}
                  >
                    <span>+</span> Create Branch
                  </button>
                )}
              </div>
              {business.branches && business.branches.length > 0 ? (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Users</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {business.branches.map((branch) => (
                        <tr key={branch.id}>
                          <td className="code-cell">BR-{String(branch.id).padStart(3, '0')}</td>
                          <td className="font-semibold">{branch.name}</td>
                          <td>{branch.email || '—'}</td>
                          <td>{branch.phone || '—'}</td>
                          <td>
                            <span className="badge">{branch.users?.length || 0}</span>
                          </td>
                          <td>
                            <span className={`status-badge ${branch.is_active ? 'active' : 'inactive'}`}>
                              {branch.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="created-at-cell">{formatDate(branch.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No branches found. {user?.role === 'super_admin' && 'Create a branch to get started.'}</p>
                </div>
              )}
            </div>

            {business.users && business.users.length > 0 && (
              <div className="details-section">
                <h3>Users</h3>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Role</th>
                        <th>Branch</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {business.users.map((userItem) => (
                        <tr key={userItem.id}>
                          <td className="font-semibold">{userItem.username}</td>
                          <td>{userItem.name || '—'}</td>
                          <td>{userItem.email || '—'}</td>
                          <td>{userItem.phone || '—'}</td>
                          <td>
                            <span className="badge">{userItem.role?.replace('_', ' ').toUpperCase()}</span>
                          </td>
                          <td>{userItem.branch?.name || '—'}</td>
                          <td>
                            <span className={`status-badge ${userItem.is_active ? 'active' : 'inactive'}`}>
                              {userItem.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="created-at-cell">{formatDate(userItem.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BusinessDetails;
